/**
 * TrackingSetupWizard Component
 * 
 * Walks users through the necessary device settings to ensure
 * reliable background mileage tracking. Similar to QuickBooks/MileIQ onboarding.
 */

import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useGeolocation } from '../../hooks/useGeolocation';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  MapPin, 
  Battery, 
  Bell, 
  Shield, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Smartphone,
  Settings,
  Navigation,
  Zap,
  RefreshCw,
  ExternalLink
} from 'lucide-react';

// Platform detection
const isIOS = Capacitor.getPlatform() === 'ios';
const isAndroid = Capacitor.getPlatform() === 'android';

// Setup steps configuration
const SETUP_STEPS = {
  ios: [
    {
      id: 'location',
      title: 'Location Access',
      subtitle: 'Allow "Always" for background tracking',
      icon: MapPin,
      instructions: [
        'Open Settings app',
        'Scroll down and tap "Servio"',
        'Tap "Location"',
        'Select "Always"',
        'Enable "Precise Location"'
      ],
      critical: true,
      checkKey: 'locationAlways'
    },
    {
      id: 'background',
      title: 'Background App Refresh',
      subtitle: 'Keep the app running in background',
      icon: RefreshCw,
      instructions: [
        'Open Settings app',
        'Tap "General"',
        'Tap "Background App Refresh"',
        'Make sure it\'s ON',
        'Find "Servio" and enable it'
      ],
      critical: true,
      checkKey: 'backgroundRefresh'
    },
    {
      id: 'battery',
      title: 'Low Power Mode',
      subtitle: 'Disable to prevent tracking interruption',
      icon: Battery,
      instructions: [
        'Open Settings app',
        'Tap "Battery"',
        'Turn OFF "Low Power Mode"',
        'Or allow Servio in Low Power Mode exceptions'
      ],
      critical: false,
      checkKey: 'batteryOptimized'
    },
    {
      id: 'notifications',
      title: 'Notifications',
      subtitle: 'Get alerts when trips are detected',
      icon: Bell,
      instructions: [
        'Open Settings app',
        'Tap "Notifications"',
        'Find "Servio"',
        'Enable "Allow Notifications"',
        'Enable "Time Sensitive Notifications"'
      ],
      critical: false,
      checkKey: 'notifications'
    }
  ],
  android: [
    {
      id: 'location',
      title: 'Location Permission',
      subtitle: 'Allow "All the time" for background tracking',
      icon: MapPin,
      instructions: [
        'Open Settings app',
        'Tap "Apps" or "Applications"',
        'Find and tap "Servio"',
        'Tap "Permissions" → "Location"',
        'Select "Allow all the time"',
        'Enable "Use precise location"'
      ],
      critical: true,
      checkKey: 'locationAlways'
    },
    {
      id: 'battery',
      title: 'Battery Optimization',
      subtitle: 'Exempt Servio from battery restrictions',
      icon: Battery,
      instructions: [
        'Open Settings app',
        'Tap "Battery" or "Battery optimization"',
        'Tap "All apps" dropdown',
        'Find "Servio"',
        'Select "Don\'t optimize" or "Unrestricted"'
      ],
      critical: true,
      checkKey: 'batteryOptimized'
    },
    {
      id: 'background',
      title: 'Background Activity',
      subtitle: 'Allow app to run in background',
      icon: Zap,
      instructions: [
        'Open Settings → Apps → Servio',
        'Tap "Battery"',
        'Enable "Allow background activity"',
        'Disable "Pause app activity if unused"'
      ],
      critical: true,
      checkKey: 'backgroundActivity'
    },
    {
      id: 'autostart',
      title: 'Auto-start Permission',
      subtitle: 'Start tracking when phone restarts',
      icon: Smartphone,
      instructions: [
        'Open Settings → Apps → Servio',
        'Look for "Auto-start" or "Startup"',
        'Enable auto-start permission',
        '(Location varies by phone manufacturer)'
      ],
      critical: false,
      checkKey: 'autostart'
    },
    {
      id: 'notifications',
      title: 'Notifications',
      subtitle: 'Required for background service',
      icon: Bell,
      instructions: [
        'Open Settings → Apps → Servio',
        'Tap "Notifications"',
        'Enable "All Servio notifications"',
        'This is required for Android background services'
      ],
      critical: true,
      checkKey: 'notifications'
    }
  ]
};

// Manufacturer-specific tips
const MANUFACTURER_TIPS = {
  samsung: {
    name: 'Samsung',
    tips: [
      'Go to Settings → Battery → Background usage limits',
      'Remove Servio from "Sleeping apps" and "Deep sleeping apps"',
      'Add Servio to "Never sleeping apps"'
    ]
  },
  xiaomi: {
    name: 'Xiaomi/MIUI',
    tips: [
      'Go to Settings → Apps → Manage apps → Servio',
      'Enable "Autostart"',
      'Set Battery saver to "No restrictions"',
      'Lock app in recent apps (swipe down on app card)'
    ]
  },
  huawei: {
    name: 'Huawei/EMUI',
    tips: [
      'Go to Settings → Battery → App launch',
      'Find Servio and set to "Manage manually"',
      'Enable Auto-launch, Secondary launch, and Run in background'
    ]
  },
  oneplus: {
    name: 'OnePlus/OxygenOS',
    tips: [
      'Go to Settings → Battery → Battery optimization',
      'Set Servio to "Don\'t optimize"',
      'In Advanced → Optimize all apps, exclude Servio'
    ]
  },
  oppo: {
    name: 'Oppo/ColorOS',
    tips: [
      'Go to Settings → Battery → Power saving',
      'Disable power saving for Servio',
      'Enable auto-startup for Servio'
    ]
  },
  pixel: {
    name: 'Google Pixel',
    tips: [
      'Go to Settings → Battery → Battery Saver',
      'Add Servio to "Unrestricted" apps',
      'Adaptive Battery may limit background apps'
    ]
  }
};

export function TrackingSetupWizard({ open, onClose, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [showManufacturerTips, setShowManufacturerTips] = useState(false);
  const { openSettings } = useGeolocation();

  const platform = isIOS ? 'ios' : 'android';
  const steps = SETUP_STEPS[platform] || SETUP_STEPS.android;
  const currentStepData = steps[currentStep];

  const handleMarkComplete = () => {
    setCompletedSteps(prev => new Set([...prev, currentStepData.id]));
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    // Store that setup was completed
    localStorage.setItem('servio_tracking_setup_complete', 'true');
    localStorage.setItem('servio_tracking_setup_date', new Date().toISOString());
    onComplete?.();
    onClose();
  };

  const criticalStepsComplete = steps
    .filter(s => s.critical)
    .every(s => completedSteps.has(s.id));

  const allStepsComplete = steps.every(s => completedSteps.has(s.id));
  const completionPercent = Math.round((completedSteps.size / steps.length) * 100);

  const IconComponent = currentStepData?.icon || Settings;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Navigation className="h-5 w-5 text-teal-500" />
            Setup Mileage Tracking
          </DialogTitle>
          <DialogDescription>
            Follow these steps to ensure reliable automatic tracking
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Setup Progress</span>
            <span className="font-medium">{completionPercent}% Complete</span>
          </div>
          <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-teal-500 transition-all duration-300"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
          <div className="flex gap-1">
            {steps.map((step, idx) => (
              <button
                key={step.id}
                onClick={() => setCurrentStep(idx)}
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  completedSteps.has(step.id) 
                    ? 'bg-green-500' 
                    : idx === currentStep 
                      ? 'bg-teal-500' 
                      : 'bg-slate-300 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Current step */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-start gap-3">
              <div className={`p-3 rounded-xl ${
                completedSteps.has(currentStepData.id) 
                  ? 'bg-green-100 dark:bg-green-900/30' 
                  : 'bg-teal-100 dark:bg-teal-900/30'
              }`}>
                {completedSteps.has(currentStepData.id) ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                ) : (
                  <IconComponent className="h-6 w-6 text-teal-600" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900 dark:text-white">
                    {currentStepData.title}
                  </h3>
                  {currentStepData.critical && (
                    <Badge variant="destructive" className="text-[10px]">Required</Badge>
                  )}
                  {completedSteps.has(currentStepData.id) && (
                    <Badge className="bg-green-100 text-green-800 text-[10px]">Done</Badge>
                  )}
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {currentStepData.subtitle}
                </p>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Instructions:
              </h4>
              <ol className="space-y-2">
                {currentStepData.instructions.map((instruction, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 flex items-center justify-center text-xs font-medium">
                      {idx + 1}
                    </span>
                    <span className="text-slate-600 dark:text-slate-400">{instruction}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={openSettings}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Settings
              </Button>
              <Button 
                className="flex-1"
                onClick={handleMarkComplete}
                disabled={completedSteps.has(currentStepData.id)}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {completedSteps.has(currentStepData.id) ? 'Completed' : 'Mark as Done'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Android manufacturer tips */}
        {isAndroid && (
          <div className="space-y-2">
            <button
              onClick={() => setShowManufacturerTips(!showManufacturerTips)}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              <Smartphone className="h-4 w-4" />
              <span>Phone-specific tips</span>
              <ChevronRight className={`h-4 w-4 transition-transform ${showManufacturerTips ? 'rotate-90' : ''}`} />
            </button>
            
            {showManufacturerTips && (
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(MANUFACTURER_TIPS).map(([key, data]) => (
                  <button
                    key={key}
                    onClick={() => {
                      // Could show a modal with detailed tips
                      alert(data.tips.join('\n\n'));
                    }}
                    className="text-left p-2 rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm"
                  >
                    <span className="font-medium">{data.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex gap-2 flex-1">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="flex-1"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={handleNext}
              disabled={currentStep === steps.length - 1}
              className="flex-1"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          
          <Button
            onClick={handleFinish}
            disabled={!criticalStepsComplete}
            className="w-full sm:w-auto"
          >
            {allStepsComplete ? 'Finish Setup' : criticalStepsComplete ? 'Skip Optional Steps' : 'Complete Required Steps'}
          </Button>
        </DialogFooter>

        {/* Warning if critical steps incomplete */}
        {!criticalStepsComplete && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-amber-800 dark:text-amber-200">
              Complete all required steps for reliable background tracking. 
              Without these, trips may not be recorded automatically.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TrackingSetupWizard;
